import { LevelSettingResponse } from "./FacilityLevelsService"
import { SensorType } from "./SensorsService"
import {
   createApiRequest,
   ApiResponse,
   Header,
   getHeaderFromSessionStorage,
} from "@/api"
import { API_URL, Endpoints } from "@/constants/endpoints"
import { HttpMethods } from "@/constants/globals"
import {
   FacilityAddress,
   FacilityForm,
   FacilityDetails,
   FacilityAlerts,
} from "@/types/facility"
import { Token } from "@/types/index"

export interface FacilityResponse {
   id: string
   name: string
   alerts: FacilityAlerts
   address: FacilityAddress
   settings: LevelSettingResponse[]
   cells?: number
   users?: number
   entraTid: string
   entraSsoActive: boolean
   consentUrl?: string
}

export interface EntraConsentResponse {
   consentUrl: string
}

export interface SensorResponse {
   id: string
   serialNumber: string
   macAddress: string
   level: string
   type: SensorType
}

export async function requestCreateFacility(
   customerId: string,
   facilityData: FacilityForm
): Promise<ApiResponse<FacilityResponse>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.FACILITIES}`,
      HttpMethods.POST,
      { ...facilityData },
      {
         headers: {
            "X-Customer-ID": customerId,
         },
      }
   )
   return response
}

export async function requestUpdateFacility(
   customerId: string,
   facilityId: string,
   facilityData: Partial<FacilityForm>
): Promise<ApiResponse<FacilityResponse>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.FACILITIES}${facilityId}/`,
      HttpMethods.PATCH,
      { ...facilityData },
      {
         headers: {
            "X-Customer-ID": customerId,
         },
      }
   )
   return response
}

export async function requestEntraConsentUrl(
   customerId: string,
   facilityId: string
): Promise<ApiResponse<EntraConsentResponse>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.FACILITIES}${facilityId}/entra/`,
      HttpMethods.GET,
      null,
      {
         headers: {
            "X-Customer-ID": customerId,
         },
      }
   )
   return response
}

export async function requestDeleteFacility(
   facilityId: string,
   customerId: string
): Promise<ApiResponse<void>> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.FACILITIES}${facilityId}/`,
      HttpMethods.DELETE,
      null,
      {
         headers: {
            "X-Customer-ID": customerId,
         },
      }
   )
   return response
}

export async function requestFacilities(): Promise<
   ApiResponse<FacilityResponse[]>
> {
   const response = await createApiRequest(
      `${API_URL}${Endpoints.FACILITIES}`,
      HttpMethods.GET,
      null,
      getHeaderFromSessionStorage(Header.CUSTOMER_ID)
   )
   return response
}

export async function requestFacilityDetails(): Promise<
   ApiResponse<FacilityDetails>
> {
   const facilityId = sessionStorage.getItem(Token.FACILITY_ID)
   const response = await createApiRequest(
      `${API_URL}${Endpoints.FACILITIES}${facilityId}/`,
      HttpMethods.GET,
      null,
      getHeaderFromSessionStorage(Header.CUSTOMER_ID)
   )
   return response
}

export async function requestSensors(): Promise<ApiResponse<SensorResponse[]>> {
   const facilityId = sessionStorage.getItem(Token.FACILITY_ID)
   const response = await createApiRequest(
      `${API_URL}${Endpoints.FACILITIES}${facilityId}/sensors/`,
      HttpMethods.GET,
      null,
      getHeaderFromSessionStorage(Header.CUSTOMER_ID)
   )
   return response
}
